import {extend, getAlphaLabelFromIndex} from 'common/util';
import {debug} from 'common/report/util';
import {reporter} from 'common/report/error-reporting';
import {insertAtIndex, removeByIndex, replaceByIndex, unique} from 'common/util/array';
import {hasUserFilters} from 'common/util/custom_events';
import {XAU_MATH_TYPES} from 'common/report/constants';
import {cohortEntryIsInvalidForDataGroup} from 'common/widgets/query-builder/util/data-groups';

import {Clause, CohortsClause, FilterClause, FormulaClause, GroupClause, ShowClause, TimeClause} from './clause';

export class Section {
  constructor(attrs = {}) {
    const clauseArgs = (Array.isArray(attrs) ? attrs : [attrs]).filter((arg) => arg instanceof Clause);

    this.clauses = [...clauseArgs, ...(attrs.clauses || [])];
  }

  get attrs() {
    const {clauses} = this;
    return {clauses};
  }

  get valid() {
    return this.clauses.every((clause) => clause.valid);
  }

  static create(sectionType, attrs) {
    switch (sectionType) {
      case ShowSection.TYPE:
        return new ShowSection(attrs);
      case CohortsSection.TYPE:
        return new CohortsSection(attrs);
      case GroupSection.TYPE:
        return new GroupSection(attrs);
      case FilterSection.TYPE:
        return new FilterSection(attrs);
      case FormulaSection.TYPE:
        return new FormulaSection(attrs);
      case TimeSection.TYPE:
        return new TimeSection(attrs);
    }
  }

  static deserialize(sectionType, clauseAttrs, syncCustomEvent = null) {
    if (Array.isArray(clauseAttrs)) {
      clauseAttrs = {clauses: clauseAttrs};
    }

    if (clauseAttrs.clauses) {
      return Section.create(
        sectionType,
        extend(clauseAttrs, {
          // pass down custom event sync function so clauses can get the latest version of each custom event
          clauses: clauseAttrs.clauses
            .map((clause) => Clause.create(sectionType, clause, syncCustomEvent))
            .filter((clause) => clause.valid),
        }),
      );
    } else {
      return {clauses: []};
    }
  }

  create() {
    return Section.create(...arguments);
  }

  serialize() {
    return this.clauses.map((c) => c.serialize());
  }

  toBookmarkData() {
    return this.clauses.map((c) => c.toBookmarkData());
  }

  toUrlData() {
    return this.clauses.map((c) => c.toUrlData());
  }

  validate(newSection) {
    const valid = newSection.valid;

    if (!valid) {
      debug.warn(`${this.TYPE} section invalid:`, newSection);
    }

    return valid ? newSection : this;
  }

  hasClauses() {
    return !!this.clauses.length;
  }

  getLastClause() {
    return this.hasClauses() ? this.clauses[this.clauses.length - 1] : null;
  }

  appendClause(newClause) {
    return this.validate(
      Section.create(this.TYPE, extend(this, {clauses: insertAtIndex(this.clauses, this.clauses.length, newClause)})),
    );
  }

  insertClause(index, newClause) {
    return this.validate(
      Section.create(this.TYPE, extend(this, {clauses: insertAtIndex(this.clauses, index, newClause)})),
    );
  }

  replaceClause(index, newClause) {
    return this.validate(
      Section.create(this.TYPE, extend(this, {clauses: replaceByIndex(this.clauses, index, newClause)})),
    );
  }

  removeClause(index) {
    return this.validate(Section.create(this.TYPE, extend(this, {clauses: removeByIndex(this.clauses, index)})));
  }

  removeEmptyClauses() {
    return this.validate(Section.create(this.TYPE, extend(this, {clauses: []})));
  }

  removeAllClauses() {
    return this.validate(Section.create(this.TYPE, extend(this, {clauses: []})));
  }

  filterClauses(filterFn) {
    return this.validate(Section.create(this.TYPE, extend(this, {clauses: this.clauses.filter(filterFn)})));
  }

  setDataGroupIdByResourceType(dataGroupId, resourceType) {
    return this.extendAllClauses((clauseIndex, clause) => {
      return clause.resourceType === resourceType ? {dataGroupId} : {};
    });
  }

  setDataGroupId(dataGroupId) {
    return this.extendAllClauses(() => ({dataGroupId}));
  }

  extendAllClauses(paramsForClause) {
    const clauses = this.clauses.map((clause, clauseIndex) => clause.extend(paramsForClause(clauseIndex, clause)));
    return this.validate(Section.create(this.TYPE, extend(this, {clauses})));
  }

  _cohortClauseIsInvalidForDataGroup({dataGroupId, cohort}) {
    return cohortEntryIsInvalidForDataGroup({cohort}, dataGroupId);
  }
}

export class ShowSection extends Section {
  get valid() {
    return super.valid && this.clauses.every((clause) => clause instanceof ShowClause);
  }

  get hasPerEventFilters() {
    return this.clauses.some((clause) => clause.filter && clause.filter.length);
  }

  _getResourceTypes() {
    return unique(
      this.clauses
        .map((clause) => clause.resourceType)
        .map((resourceType) =>
          resourceType === Clause.RESOURCE_TYPE_ALL ? Clause.RESOURCE_TYPE_EVENTS : resourceType,
        ),
    );
  }

  isEventsOnlyQuery() {
    const resourceTypes = this._getResourceTypes();
    return resourceTypes.length === 1 && resourceTypes[0] === Clause.RESOURCE_TYPE_EVENTS;
  }

  isPeopleOnlyQuery() {
    const resourceTypes = this._getResourceTypes();
    return resourceTypes.length === 1 && resourceTypes[0] === Clause.RESOURCE_TYPE_PEOPLE;
  }

  hasCohortsOverTime() {
    return this._getResourceTypes().includes(Clause.prototype.RESOURCE_TYPE_COHORTS);
  }

  hasClausesInvalidForDataGroups(customEvents) {
    return this.clauses.some((clause) => {
      return this._clauseInvalidForDataGroup({customEvents, clause, dataGroupId: undefined});
    });
  }

  removeEventFilters() {
    return this.extendAllClauses(() => ({filter: []}));
  }

  makeValidForDataGroup(dataGroupId, customEvents) {
    let newSection = this.filterClauses((clause) => {
      return !this._clauseInvalidForDataGroup({dataGroupId, customEvents, clause});
    });
    if (newSection.clauses.length === 0) {
      newSection = ShowSection.create();
    }
    return newSection;
  }

  _clauseInvalidForDataGroup({dataGroupId, customEvents, clause}) {
    const {resourceType, property, value} = clause;
    return (
      (resourceType === Clause.prototype.RESOURCE_TYPE_COHORTS &&
        this._cohortClauseIsInvalidForDataGroup({dataGroupId, cohort: value})) ||
      // Detects People property ("All People" is supported by group-level analysis)
      (resourceType === Clause.prototype.RESOURCE_TYPE_PEOPLE && property && dataGroupId) ||
      // Custom events that are based on user properties
      (dataGroupId && value.custom && hasUserFilters(value, customEvents))
    );
  }

  hasXauClauses() {
    return this.clauses.some((clause) => clause.isXauClause());
  }

  lowestXauTimeUnit() {
    // time unit is the minimum unit of all XAU clauses, e.g. a DAU and WAU report's time unit = `day`
    const xauOperators = this.clauses
      .map((clause) => clause.math)
      .filter((math) => ShowClause.XAU_ONLY_MATH_TYPES.includes(math));
    let unit;
    if (xauOperators.includes(XAU_MATH_TYPES.DAU)) {
      return XAU_MATH_TYPES.DAU;
    } else if (xauOperators.includes(XAU_MATH_TYPES.WAU)) {
      return XAU_MATH_TYPES.WAU;
    } else if (xauOperators.includes(XAU_MATH_TYPES.MAU)) {
      return XAU_MATH_TYPES.MAU;
    } else {
      reporter.error(`Unhandled XAU math type in "${xauOperators}"`);
      return unit;
    }
  }

  addFormulaLabels() {
    return this.extendAllClauses((clauseIndex) => ({formulaLabel: ShowClause.computeFormulaLabel(clauseIndex)}));
  }

  removeFormulaLabels() {
    return this.extendAllClauses(() => ({formulaLabel: null}));
  }

  toBookmarkData() {
    let clauses = super.toBookmarkData();
    if (clauses.length > 1) {
      clauses = clauses.map((clause, index) => ({
        ...clause,
        labelPrefix: `${getAlphaLabelFromIndex(index)}. `,
      }));
    }

    return clauses;
  }

  static create({
    dataset = null,
    dataGroupId = null,
    value = null,
    math = ShowClause.MATH_TYPE_TOTAL,
    resourceType = Clause.RESOURCE_TYPE_EVENTS,
  } = {}) {
    if (value) {
      return new ShowSection(new ShowClause({dataset, dataGroupId, math, resourceType, value}));
    }
    return new ShowSection();
  }
}
ShowSection.TYPE = ShowSection.prototype.TYPE = ShowClause.TYPE;

// Cohort Breakdowns (group bys). Only separate from "GroupSection" for historical reasons
export class CohortsSection extends Section {
  get valid() {
    return super.valid && this.clauses.every((clause) => clause instanceof CohortsClause);
  }

  makeValidForDataGroup(dataGroupId) {
    return this.filterClauses((clause) => !this._cohortClauseIsInvalidForDataGroup({cohort: clause, dataGroupId}));
  }

  hasClausesInvalidForDataGroups() {
    return this.clauses.some((clause) => {
      return this._cohortClauseIsInvalidForDataGroup({cohort: clause, dataGroupId: undefined});
    });
  }

  // return cohort id as negative value if negated is true
  getCohortIdByName(cohortName) {
    const firstMatchingCohort = this.clauses.find((clause) => clause.formattedName() === cohortName);
    return firstMatchingCohort && firstMatchingCohort.id * (firstMatchingCohort.negated ? -1 : 1);
  }
}
CohortsSection.TYPE = CohortsSection.prototype.TYPE = CohortsClause.TYPE;

export class GroupSection extends Section {
  get valid() {
    return super.valid && this.clauses.every((clause) => clause instanceof GroupClause);
  }

  isPeopleTimeSeries() {
    return this.clauses.length && this.clauses[this.clauses.length - 1].propertyType === `datetime`;
  }

  hasUserClauses() {
    return this.clauses.some((clause) => this._isUserClause(clause));
  }

  removeUserClauses(dataGroupId) {
    if (dataGroupId) {
      return this.filterClauses((clause) => !this._isUserClause(clause));
    } else {
      return this;
    }
  }

  _isUserClause({resourceType, dataGroupId}) {
    return resourceType === Clause.prototype.RESOURCE_TYPE_PEOPLE && !dataGroupId;
  }
}
GroupSection.TYPE = GroupSection.prototype.TYPE = GroupClause.TYPE;

export class FilterSection extends Section {
  constructor(attrs = {}) {
    super(...arguments);
    // The determiner could be saved on the attrs object OR on the first filter clause.
    const savedDeterminer = (attrs && attrs.determiner) || (this.clauses.length && this.clauses[0].determiner);
    if (savedDeterminer && FilterSection.DETERMINER_TYPES.includes(savedDeterminer)) {
      this.determiner = savedDeterminer;
    } else {
      this.determiner = FilterSection.DETERMINER_ALL;
    }

    if (this.clauses && this.clauses.length < 2) {
      // reset to all when only one clause
      this.determiner = FilterSection.DETERMINER_ALL;
    }

    if (this.clauses.length) {
      this.clauses[0].determiner = this.determiner;
    }
  }

  get valid() {
    return (
      super.valid &&
      this.clauses.every((clause) => clause instanceof FilterClause) &&
      FilterSection.DETERMINER_TYPES.includes(this.determiner)
    );
  }

  hasClausesInvalidForDataGroups() {
    return this.clauses.some((clause) => this._clauseInvalidForDataGroup({clause, dataGroupId: undefined}));
  }

  removeMultipleCohortFilters() {
    return this.filterClauses((clause) => !(clause.value === FilterClause.COHORTS && clause.filterValue.length > 1));
  }

  makeValidForDataGroup(dataGroupId) {
    return this.filterClauses((clause) => !this._clauseInvalidForDataGroup({clause, dataGroupId}));
  }

  _clauseInvalidForDataGroup({clause, dataGroupId}) {
    const {resourceType, propertyFilter} = clause;
    const cohort =
      propertyFilter &&
      propertyFilter.filterValue &&
      propertyFilter.filterValue[0] &&
      propertyFilter.filterValue[0].cohort;
    return (
      (cohort && this._cohortClauseIsInvalidForDataGroup({dataGroupId, cohort})) ||
      (resourceType === Clause.prototype.RESOURCE_TYPE_PEOPLE && !clause.dataGroupId)
    );
  }

  serialize() {
    const clauses = super.serialize();
    if (clauses.length > 1) {
      clauses[0].determiner = this.determiner;
    }
    return clauses;
  }

  toBookmarkData() {
    const clauses = super.toBookmarkData();
    if (clauses.length > 1) {
      clauses[0].determiner = this.determiner;
    }
    return clauses;
  }

  toUrlData() {
    const clauses = super.toUrlData();
    return clauses.length ? {clauses, determiner: this.determiner} : {};
  }
}

FilterSection.TYPE = FilterSection.prototype.TYPE = FilterClause.TYPE;
FilterSection.DETERMINER_ALL = FilterSection.prototype.DETERMINER_ALL = `all`;
FilterSection.DETERMINER_ANY = FilterSection.prototype.DETERMINER_ANY = `any`;
FilterSection.DETERMINER_TYPES = FilterSection.prototype.DETERMINER_TYPES = [
  FilterSection.DETERMINER_ALL,
  FilterSection.DETERMINER_ANY,
];

export class FormulaSection extends Section {
  get valid() {
    return super.valid && this.clauses.every((clause) => clause instanceof FormulaClause);
  }
}
FormulaSection.TYPE = FormulaSection.prototype.TYPE = FormulaClause.TYPE;

export class TimeSection extends Section {
  get valid() {
    return super.valid && this.clauses.length === 1 && this.clauses.every((clause) => clause instanceof TimeClause);
  }
}
TimeSection.TYPE = TimeSection.prototype.TYPE = TimeClause.TYPE;
